"use client";

import Chevron from "@/app/assets/images/svg/chevron.svg";
import { useSearchContext } from "@/components/_Search/provider/SearchContextProvider";
import { SearchFacet, SearchFacetOption } from "@/lib/_search-providers/types";
import * as Accordion from "@radix-ui/react-accordion";
import { FC, useMemo } from "react";
import Facet from "./Facet/Facet";
import styles from "./ToggleFilterAccordian.module.scss";
import { useScopedI18n } from "@/i18n/client";

interface Props {
	facet: SearchFacet;
}

const ToggleFilterAccordion: FC<Props> = ({ facet }) => {
	const { facets } = useSearchContext();

	const t = useScopedI18n("FilterAndSort.facets");

	if (!facet) return null;

	// Filter out any accordions we don't want to show here:
	if (facet && ["categories_hierarchy", "category_ids"].includes(facet.value))
		return null;

	const label = useMemo(() => {
		const key = `labels.${facet.label}`;

		const label = t(key as any);

		// if the translated values matches the key, just return the facet label
		// as this means we don't have a translation for it
		return label === key ? facet.label : label;
	}, [t, facet]);

	const handleOnSelectFacet = (option: SearchFacetOption, active: boolean) => {
		facets.updateFacet(facet.value, option.value, active);
	};

	return (
		<Accordion.Root
			type='single'
			collapsible
			className={styles.accordionRoot}
			defaultValue={label}
		>
			<Accordion.Item value={label} className={styles.AccordionItem}>
				<Accordion.Header className={styles.AccordionHeader}>
					<Accordion.Trigger className={styles.AccordionTrigger}>
						<span className={styles.label}>{label}</span>

						<Chevron aria-labelledby='directional chevron' />
					</Accordion.Trigger>
				</Accordion.Header>
				<Accordion.Content className={styles.AccordionContent}>
					{facet.options?.map((option) => (
						<Facet
							key={`${facet.value}:${option.value}`}
							option={option}
							onSelectFacet={(active) => handleOnSelectFacet(option, active)}
						/>
					))}
				</Accordion.Content>
			</Accordion.Item>
		</Accordion.Root>
	);
};

export default ToggleFilterAccordion;
